/** Chat panel for AI debugging tools — thread-aware, adapter-driven.
 *
 *  A thread is a chain of background tasks anchored on its first task; the
 *  anchor's id doubles as the thread id and is known synchronously (it IS
 *  the first task we start). Follow-ups send `thread_task_id` and the
 *  backend replays the captured context plus the prior Q&A.
 *
 *  Rendering sources: completed/failed turns come from the bgtasks listing
 *  (filtered to the thread) plus per-task results (cached forever — results
 *  are immutable); the in-flight turn is local state polled via the
 *  standard retrieve/result pattern. Answers are AI-generated from captured
 *  (untrusted) data and render through the shared no-innerHTML <Markdown>.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  MenuItem,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ShareIcon from '@mui/icons-material/Share';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ClipboardEvent, type ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';
import { useLocation } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState, TASK_STATE } from '@/api/bgtasks';
import type { BackgroundTaskRecord } from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';
import { useOptionalAuth } from '@/auth/AuthProvider';
import type { DiagnoseAttachment } from '@/api/diagnoseAttachments';
import { Markdown } from '@/components/Markdown';
import { SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import { formatDate } from '@/utils/format';
import { downloadBlob } from '@/utils/download';
import { formatBytes } from '@/utils/formatBytes';

import type { AiDiagAdapter, AiDiagCopy, DiagnoseResult, DiagnoseTaskParams } from './types';
import { AttachmentChips } from './attachments/AttachmentChips';
import { AttachmentTray } from './attachments/AttachmentTray';
import { clipboardFiles, hasPlainText } from './attachments/clipboard';
import { DropOverlay } from './attachments/DropOverlay';
import { useAttachmentStaging } from './attachments/useAttachmentStaging';
import { ContextPanel } from './context/ContextPanel';
import { ExportMenu } from './export/ExportMenu';
import { buildThreadPdf } from './export/pdf/exportPdf';
import { MessageActions } from './export/MessageActions';
import {
  exportFileName,
  threadToMarkdown,
  turnAnswerMarkdown,
  turnQuestionMarkdown,
  type ExportTurn,
} from './export/threadMarkdown';
import { useThreadParam } from './useThreadParam';

const errorText = (e: unknown) =>
  e instanceof ConsoleApiError ? ((e.body?.detail as string) ?? e.message) : (e as Error).message;

function availabilityChipColor(status: string): 'default' | 'warning' | 'error' {
  if (status === 'ok') return 'default';
  if (status === 'error') return 'error';
  return 'warning';
}

function AvailabilityChips({ result }: { result: DiagnoseResult }) {
  const truncated = new Set(
    result.context_stats.sections.filter((s) => (s.truncated_chars ?? 0) > 0).map((s) => s.name),
  );
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {Object.entries(result.availability).map(([source, status]) => (
        <Tooltip
          key={source}
          title={
            truncated.has(source)
              ? `${source}: ${status} (truncated to fit the context budget)`
              : `${source}: ${status}`
          }
        >
          <Chip
            size="small"
            label={`${source}: ${status}`}
            color={availabilityChipColor(status)}
            variant="outlined"
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

function QuestionBubble({
  question,
  meta,
  attachments,
  taskId,
  actions,
}: {
  question: string;
  meta?: string;
  attachments?: DiagnoseAttachment[];
  /** The turn's task — any thread member can serve its files. */
  taskId: string;
  actions?: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <Box sx={{ maxWidth: '85%', bgcolor: 'action.hover', borderRadius: 2, px: 2, py: 1 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {question}
        </Typography>
        {attachments && attachments.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <AttachmentChips attachments={attachments} taskId={taskId} />
          </Box>
        )}
        {meta && (
          <Typography variant="caption" color="text.secondary">
            {meta}
          </Typography>
        )}
      </Box>
      {actions}
    </Box>
  );
}

function AnswerBubble({
  result,
  actions,
  evidence,
  footer,
}: {
  result: DiagnoseResult;
  actions?: ReactNode;
  /** Show the evidence chips (diagnose tools; not the console agent). */
  evidence: boolean;
  footer?: ReactNode;
}) {
  return (
    <Box>
      <Markdown source={result.diagnostic_md} />
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {result.model} · ${result.cost_usd.toFixed(4)}
        </Typography>
        {actions}
        {evidence && (result.turn === 1 || result.context_source === 'rebuilt') && (
          <AvailabilityChips result={result} />
        )}
        {evidence && result.context_source === 'rebuilt' && (
          <Chip size="small" color="warning" variant="outlined" label="context re-captured" />
        )}
      </Stack>
      {footer}
    </Box>
  );
}

/** The diagnose wording — every key of a tool's `copy` falls back to it. */
function wording(copy: AiDiagCopy | undefined, subjectNoun: string): Required<AiDiagCopy> {
  return {
    title: `Ask about this ${subjectNoun}`,
    threadTitle: 'Diagnosis thread',
    questionLabel: 'Question or complaint',
    followUpLabel: 'Follow-up question',
    submit: 'Diagnose',
    submitFollowUp: 'Ask follow-up',
    running: 'Diagnosing…',
    runningHint: '(typically 30–120 seconds)',
    notEnabled: 'AI diagnostics is not enabled on this deployment.',
    linkNoun: 'diagnosis',
    runNoun: 'diagnosis',
    threadGone: 'That diagnosis thread no longer exists.',
    otherSubject: `That diagnosis thread belongs to a different ${subjectNoun}.`,
    ...copy,
  };
}

interface TurnView {
  taskId: string;
  question: string;
  attachments: DiagnoseAttachment[];
  createdTs: string | null;
  userEmail: string | null;
  durationSeconds: number | null;
  /** What the run spent per its record (failed/cancelled included); null
   *  when the backend does not report it. */
  recordCost: number | null;
  result?: DiagnoseResult;
  failedMessage?: string;
  cancelled?: boolean;
}

export function AiDiagChat({ adapter, subjectNote }: { adapter: AiDiagAdapter; subjectNote?: string }) {
  const snackbar = useSnackbar();
  const auth = useOptionalAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');
  const [model, setModel] = useState('');
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    taskId: string;
    question: string;
    attachments?: DiagnoseAttachment[];
  } | null>(null);
  const [budget, setBudget] = useState('');
  const [tab, setTab] = useState<'chat' | 'evidence'>('chat');
  const [previewTaskId, setPreviewTaskId] = useState<string | null>(null);
  useThreadParam(anchorId, setAnchorId);
  const evidence = adapter.evidence !== false;
  const words = wording(adapter.copy, adapter.subjectNoun);

  const ctxQuery = useQuery({
    queryKey: ['ai-diag', adapter.taskType, 'context', ...(adapter.contextKey ? [adapter.contextKey] : [])],
    queryFn: adapter.launchContext,
    staleTime: 5 * 60_000,
  });
  const ctx = ctxQuery.data;
  const defaultModel = ctx?.models.find((m) => m.default)?.id ?? '';

  // All of this tool's tasks for this subject — feeds both the current
  // thread's history and the "previous threads" list.
  const tasksQuery = useQuery({
    queryKey: ['ai-diag', adapter.taskType, 'tasks', adapter.labelContains ?? ''],
    queryFn: () =>
      bgtasksApi.list({
        task_type: adapter.taskType,
        task_label_contains: adapter.labelContains,
        page_size: 100,
        ordering: 'created_ts',
      }),
    staleTime: 30_000,
  });
  const allSubjectTasks = useMemo(
    () => (tasksQuery.data?.results ?? []).filter((r) => adapter.matchesSubject(r.params)),
    [tasksQuery.data, adapter],
  );
  const isPreview = (r: { params: Record<string, unknown> | null }) =>
    Boolean((r.params as { preview?: boolean } | null)?.preview);
  // A context preview has no question and no answer, so it is not a thread
  // turn — it must not appear as one in the history or the turn list.
  const subjectTasks = useMemo(() => allSubjectTasks.filter((r) => !isPreview(r)), [allSubjectTasks]);
  /** The most recent context preview, if any — the Evidence tab falls back to
   *  it when no thread is open. */
  const latestPreviewId = useMemo(() => {
    const previews = allSubjectTasks.filter((r) => isPreview(r) && r.state === TASK_STATE.COMPLETED);
    return previews.length > 0 ? (previews[previews.length - 1]?.id ?? null) : null;
  }, [allSubjectTasks]);

  // The thread itself, fetched by its anchor rather than filtered out of the
  // listing above: a shared link's thread may be older than that page.
  const threadQuery = useQuery({
    queryKey: ['ai-diag', adapter.taskType, 'thread', anchorId],
    queryFn: () =>
      bgtasksApi.list({
        task_type: adapter.taskType,
        thread_task_id: anchorId as string,
        page_size: 100,
        ordering: 'created_ts',
      }),
    enabled: anchorId != null,
    staleTime: 30_000,
  });

  const threadMembers = useMemo(() => {
    if (anchorId == null) return [];
    const fetched = threadQuery.data?.results;
    if (fetched?.length) return fetched;
    return subjectTasks.filter(
      (r) => r.id === anchorId || (r.params as { thread_task_id?: string } | null)?.thread_task_id === anchorId,
    );
  }, [subjectTasks, anchorId, threadQuery.data]);

  // A link that cannot be honoured must say so. Silently rendering an empty
  // panel is indistinguishable from a broken feature.
  useEffect(() => {
    if (anchorId == null || !threadQuery.isSuccess) return;
    const rows = threadQuery.data.results ?? [];
    const anchor = rows.find((r) => r.id === anchorId);
    if (anchor == null) {
      snackbar.show(words.threadGone, 'warning');
      setAnchorId(null);
      return;
    }
    if (!adapter.matchesSubject(anchor.params)) {
      snackbar.show(words.otherSubject, 'warning');
      setAnchorId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorId, threadQuery.isSuccess, threadQuery.data]);

  // Results of completed thread members (immutable — cache forever).
  const memberResults = useQueries({
    queries: threadMembers.map((r) => ({
      queryKey: ['ai-diag', 'result', r.id],
      queryFn: async () => (await bgtasksApi.result(r.id)).result as unknown as DiagnoseResult,
      enabled: r.state === TASK_STATE.COMPLETED,
      staleTime: Infinity,
      retry: (failureCount: number, error: unknown) =>
        failureCount < 10 && error instanceof ConsoleApiError && error.status === 404,
      retryDelay: 1200,
    })),
  });

  // The in-flight turn (started here or adopted from a 409).
  const taskQuery = useQuery({
    queryKey: ['ai-diag', adapter.taskType, 'task', pending?.taskId],
    queryFn: () => bgtasksApi.retrieve(pending?.taskId as string),
    enabled: pending != null,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state == null || isActiveState(state) ? 2000 : false;
    },
  });
  const pendingTask = taskQuery.data;
  const pendingDone = pendingTask != null && !isActiveState(pendingTask.state);

  // Adopted tasks: learn the real question/thread from the record itself.
  useEffect(() => {
    if (pending == null || pendingTask == null) return;
    const params = pendingTask.params as DiagnoseTaskParams | null;
    if (pending.question === '…' && params?.question) {
      setPending({ taskId: pending.taskId, question: params.question, attachments: params.attachments });
    }
    if (anchorId == null) {
      setAnchorId(params?.thread_task_id ?? pending.taskId);
    }
  }, [pending, pendingTask, anchorId]);

  // When the in-flight turn settles, fold it into the listed history.
  useEffect(() => {
    if (pending == null || pendingTask == null || !pendingDone) return;
    if (pendingTask.state === TASK_STATE.COMPLETED) {
      void queryClient.invalidateQueries({ queryKey: ['ai-diag', adapter.taskType, 'tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['ai-diag', adapter.taskType, 'thread'] });
      setPending(null);
      setQuestion('');
    }
    // Failed/cancelled turns stay in `pending` so the inline alert shows;
    // dismissing/retrying clears them.
  }, [pending, pendingTask, pendingDone, queryClient, adapter.taskType]);

  // Files staged for the question being written; the open thread's own files
  // count toward its per-thread cap.
  const threadAttachmentCount = useMemo(
    () =>
      threadMembers.reduce(
        (n, r) => n + ((r.params as DiagnoseTaskParams | null)?.attachments?.length ?? 0),
        0,
      ),
    [threadMembers],
  );
  const attachCaps = ctx?.caps.attachments;
  const attachEnabled = attachCaps?.enabled === true;
  const onAttachRejected = useCallback((m: string) => snackbar.show(m, 'warning'), [snackbar]);
  const staging = useAttachmentStaging({
    caps: attachCaps,
    alreadyInThread: threadAttachmentCount,
    onRejected: onAttachRejected,
  });
  // Drop anywhere on the chat; the paperclip opens the same picker.
  const dropzone = useDropzone({
    onDrop: (files: File[]) => staging.add(files),
    noClick: true,
    noKeyboard: true,
    multiple: true,
    // Same rule as the question field: closed only while a turn is running.
    disabled:
      !attachEnabled ||
      (pending != null &&
        !(pendingTask?.state === TASK_STATE.FAILED || pendingTask?.state === TASK_STATE.CANCELLED)),
  });

  const start = useMutation({
    mutationFn: (body: { question: string; attachments: DiagnoseAttachment[] }) =>
      adapter.start({
        question: body.question,
        model: model || undefined,
        thread_task_id: anchorId ?? undefined,
        budget_usd: budget.trim() ? Number(budget) : undefined,
        attachments: body.attachments.length > 0 ? body.attachments.map((a) => a.id) : undefined,
      }),
    onSuccess: (r, body) => {
      setPending({ taskId: r.task_id, question: body.question, attachments: body.attachments });
      staging.reset();
      if (anchorId == null) setAnchorId(r.task_id); // the first task IS the anchor
      void queryClient.invalidateQueries({ queryKey: ['ai-diag', adapter.taskType, 'context'] });
    },
    onError: (e: unknown) => {
      const conflictTaskId =
        e instanceof ConsoleApiError && e.status === 409
          ? (e.body as { task_id?: string } | undefined)?.task_id
          : undefined;
      if (conflictTaskId) {
        setPending({ taskId: conflictTaskId, question: '…' });
        snackbar.show(`A ${words.runNoun} was already running — showing it.`, 'info');
        return;
      }
      snackbar.show(errorText(e), 'error');
    },
  });

  /** Build the evidence without asking the model anything. The task is the
   *  same one a diagnosis runs, stopped before the model call, so the
   *  Evidence tab reads it through the identical path. */
  const preview = useMutation({
    mutationFn: (materializeAll: boolean) =>
      adapter.start({ question: '', preview: true, materialize_all: materializeAll }),
    onSuccess: (r) => {
      setPreviewTaskId(r.task_id);
      setTab('evidence');
      void queryClient.invalidateQueries({ queryKey: ['ai-diag', adapter.taskType, 'tasks'] });
    },
    onError: (e: unknown) => snackbar.show(errorText(e), 'error'),
  });

  const cancel = useMutation({
    mutationFn: () => bgtasksApi.cancel(pending?.taskId as string),
    onError: (e) => snackbar.show(errorText(e), 'error'),
  });

  // -- view assembly ---------------------------------------------------------

  const turns: TurnView[] = useMemo(() => {
    // memberResults is index-aligned with threadMembers (useQueries order).
    const resultById = new Map<string, DiagnoseResult>();
    threadMembers.forEach((r, i) => {
      const data = memberResults[i]?.data;
      if (data) resultById.set(r.id, data);
    });
    return threadMembers
      .filter((r) => r.id !== pending?.taskId)
      .map((r) => {
        const params = r.params as DiagnoseTaskParams | null;
        const view: TurnView = {
          taskId: r.id,
          question: params?.question ?? '(question unavailable)',
          attachments: params?.attachments ?? [],
          createdTs: r.created_ts,
          userEmail: r.user_email,
          durationSeconds: r.duration_seconds,
          recordCost: r.cost_usd ?? null,
        };
        if (r.state === TASK_STATE.COMPLETED) view.result = resultById.get(r.id);
        else if (r.state === TASK_STATE.FAILED) view.failedMessage = r.error_message || 'The turn failed.';
        else if (r.state === TASK_STATE.CANCELLED) view.cancelled = true;
        return view;
      });
  }, [threadMembers, memberResults, pending?.taskId]);

  // The open thread's settled results, for a host that shows more than the chat
  // (the console agent's proposal panel). Keyed on the result ids so a refetch
  // of the same results does not call back again.
  const { onThreadResults } = adapter;
  const settledResults = useMemo(
    () => turns.filter((t) => t.result).map((t) => t.result as DiagnoseResult),
    [turns],
  );
  const settledKey = `${anchorId ?? ''}:${turns.filter((t) => t.result).map((t) => t.taskId).join(',')}`;
  useEffect(() => {
    onThreadResults?.(anchorId ? settledResults : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledKey, onThreadResults]);

  // Everything the thread spent: each run's recorded spend (failed and
  // cancelled runs included — they cost money too), falling back to the
  // result's cost on a backend that does not report it.
  const costSoFar = threadMembers.reduce(
    (sum, r, i) => sum + (r.cost_usd ?? memberResults[i]?.data?.cost_usd ?? 0),
    0,
  );

  // -- export ------------------------------------------------------------------

  const modelLabel = (id: string | undefined) =>
    id ? (ctx?.models.find((m) => m.id === id)?.label ?? id) : undefined;
  /** Settled turns only — an in-flight answer is not exported. */
  const exportTurns: ExportTurn[] = turns
    .filter((t) => t.result || t.failedMessage || t.cancelled)
    .map((t, i) => ({
      index: i + 1,
      taskId: t.taskId,
      question: t.question,
      attachments: t.attachments,
      createdTs: t.createdTs,
      userEmail: t.userEmail,
      durationSeconds: t.durationSeconds,
      status: t.result ? 'completed' : t.cancelled ? 'cancelled' : 'failed',
      failedMessage: t.failedMessage,
      answerMd: t.result?.diagnostic_md,
      model: modelLabel(t.result?.model),
      costUsd: t.recordCost ?? t.result?.cost_usd ?? null,
      toolCalls: t.result?.tool_calls,
    }));
  const exportById = new Map(exportTurns.map((t) => [t.taskId, t]));
  const canExport = exportTurns.some((t) => t.status === 'completed');
  const exportMeta = () => ({
    subjectTitle: adapter.subjectTitle,
    // The router's location, which carries ?thread (and ?scope).
    threadUrl: anchorId ? `${window.location.origin}${location.pathname}${location.search}` : null,
    exportedAt: new Date(),
    exportedBy: auth?.user?.email ?? null,
  });
  const messageFile = (t: ExportTurn, what: 'question' | 'answer') =>
    exportFileName(adapter.subjectTitle, 'md', new Date(), `turn-${t.index}-${what}`);
  const pdfExport = useMutation({
    mutationFn: () => buildThreadPdf(exportMeta(), exportTurns, window.location.origin),
    onMutate: () => snackbar.show('Preparing the PDF…', 'info'),
    onSuccess: (blob) => downloadBlob(blob, exportFileName(adapter.subjectTitle, 'pdf', new Date())),
    onError: () => snackbar.show('The PDF could not be created.', 'error'),
  });
  const lastModel = [...turns].reverse().find((t) => t.result)?.result?.model;

  const previousThreads = useMemo(
    () =>
      subjectTasks
        .filter((r) => !(r.params as { thread_task_id?: string } | null)?.thread_task_id)
        .filter((r) => r.id !== anchorId && r.state === TASK_STATE.COMPLETED)
        .reverse(),
    [subjectTasks, anchorId],
  );

  const pendingFailed =
    pending != null && pendingTask != null && pendingTask.state === TASK_STATE.FAILED;
  const pendingCancelled =
    pending != null && pendingTask != null && pendingTask.state === TASK_STATE.CANCELLED;
  const running = pending != null && !pendingFailed && !pendingCancelled;

  const dismissFailedTurn = () => {
    // A failed anchor can't take follow-ups — the next question starts fresh.
    if (pending != null && anchorId === pending.taskId) setAnchorId(null);
    setPending(null);
  };

  const newThread = () => {
    setAnchorId(null);
    setPending(null);
    setQuestion('');
    staging.reset();
  };

  /** The live URL — it already carries `?thread` (useThreadParam) and
   *  `?scope` (TenantScopeUrlSync), so the recipient lands on this exact
   *  conversation with the same tenant scope. */
  const copyThreadLink = () => {
    navigator.clipboard.writeText(window.location.href).then(
      () => snackbar.show(`Link to this ${words.linkNoun} copied.`, 'success'),
      () => snackbar.show('Could not copy the link.', 'error'),
    );
  };

  // -- render -----------------------------------------------------------------

  if (ctxQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (ctxQuery.error || !ctx) {
    return <Alert severity="error">Couldn’t load the AI diagnostics context.</Alert>;
  }
  if (!ctx.enabled) {
    return <Alert severity="info">{words.notEnabled}</Alert>;
  }

  const canSubmit =
    !running &&
    question.trim().length > 0 &&
    question.length <= ctx.caps.max_question_chars &&
    !staging.uploading &&
    !staging.hasErrors;

  /** A pasted screenshot (or copied files) attaches; text still pastes. */
  const onQuestionPaste = (e: ClipboardEvent) => {
    if (!attachEnabled || running) return;
    const files = clipboardFiles(e.clipboardData);
    if (files.length === 0) return;
    if (!hasPlainText(e.clipboardData)) e.preventDefault();
    staging.add(files);
  };
  const isFollowUp = anchorId != null && (turns.length > 0 || pending != null);
  // The evidence of the OPEN thread, else the preview this session started,
  // else the latest stored preview for this subject.
  const evidenceTaskId = anchorId ?? previewTaskId ?? latestPreviewId;

  return (
    <Box>
      <SectionCard
        title={isFollowUp ? words.threadTitle : words.title}
        action={
          isFollowUp ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              {costSoFar > 0 && (
                <Typography variant="caption" color="text.secondary">
                  thread cost ${costSoFar.toFixed(4)}
                </Typography>
              )}
              {canExport && (
                <ExportMenu
                  markdown={() => threadToMarkdown(exportMeta(), exportTurns)}
                  filename={() => exportFileName(adapter.subjectTitle, 'md', new Date())}
                  extraItems={(close) => (
                    <MenuItem
                      disabled={pdfExport.isPending}
                      onClick={() => {
                        close();
                        pdfExport.mutate();
                      }}
                    >
                      {pdfExport.isPending ? 'Preparing PDF…' : 'Download PDF'}
                    </MenuItem>
                  )}
                />
              )}
              <Tooltip title={`Copy a link to this ${words.linkNoun}`}>
                <IconButton
                  size="small"
                  onClick={copyThreadLink}
                  aria-label={`Copy a link to this ${words.linkNoun}`}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" onClick={newThread}>
                New thread
              </Button>
            </Stack>
          ) : undefined
        }
      >
        {evidence && (
          <Tabs
            value={tab}
            onChange={(_e, v: 'chat' | 'evidence') => setTab(v)}
            sx={{ mb: 1, minHeight: 36 }}
          >
            <Tab value="chat" label="Chat" sx={{ minHeight: 36 }} />
            <Tab
              value="evidence"
              label="Evidence"
              sx={{ minHeight: 36 }}
              title="What the AI was given, and what it could fetch"
            />
          </Tabs>
        )}

        {evidence && tab === 'evidence' ? (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                disabled={preview.isPending || running}
                onClick={() => preview.mutate(false)}
              >
                Preview context
              </Button>
              <Button
                size="small"
                disabled={preview.isPending || running}
                onClick={() => preview.mutate(true)}
              >
                Preview + fetch every section
              </Button>
              <Typography variant="caption" color="text.secondary">
                Assembles the evidence with no model call and no cost.
              </Typography>
            </Stack>
            {preview.isPending && <Alert severity="info">Assembling the evidence…</Alert>}
            <ContextPanel taskId={evidenceTaskId} tenant={adapter.tenantId ?? undefined} />
          </Stack>
        ) : (
        <Box
          {...(attachEnabled ? dropzone.getRootProps() : {})}
          sx={{ position: 'relative' }}
        >
        {attachEnabled && <input {...dropzone.getInputProps()} aria-label="Attach files" />}
        {attachEnabled && dropzone.isDragActive && <DropOverlay />}
        <Stack spacing={2.5}>
          {evidence && ctx.logs_available === false && turns.length === 0 && !pending && (
            <Alert severity="warning">
              Backend logs are not queryable from this deployment — diagnostics run on the
              archive and template history only.
            </Alert>
          )}
          {evidence && isFollowUp && (
            <Alert severity="info" sx={{ py: 0 }}>
              Follow-ups reuse the evidence captured at the thread&apos;s first question — start a
              new thread to re-capture current logs and templates.
            </Alert>
          )}

          {turns.map((turn) => {
            const ex = exportById.get(turn.taskId);
            return (
            <Stack key={turn.taskId} spacing={1.5}>
              <QuestionBubble
                question={turn.question}
                attachments={turn.attachments}
                taskId={turn.taskId}
                actions={
                  ex && (
                    <MessageActions
                      what="question"
                      markdown={turnQuestionMarkdown(ex)}
                      filename={messageFile(ex, 'question')}
                    />
                  )
                }
              />
              {turn.result ? (
                <AnswerBubble
                  result={turn.result}
                  evidence={evidence}
                  footer={adapter.renderAnswerFooter?.(turn.result)}
                  actions={
                    ex && (
                      <MessageActions
                        what="answer"
                        markdown={turnAnswerMarkdown(ex)}
                        filename={messageFile(ex, 'answer')}
                      />
                    )
                  }
                />
              ) : turn.failedMessage ? (
                <Alert severity="error">{turn.failedMessage}</Alert>
              ) : turn.cancelled ? (
                <Alert severity="warning">This turn was cancelled.</Alert>
              ) : (
                <LinearProgress />
              )}
            </Stack>
            );
          })}

          {pending && (
            <Stack spacing={1.5}>
              <QuestionBubble
                question={pending.question}
                attachments={pending.attachments}
                taskId={pending.taskId}
              />
              {running ? (
                <Stack spacing={1}>
                  <LinearProgress />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {pendingTask?.progress?.step ?? 'Queued…'} {words.runningHint}
                    </Typography>
                    <Button size="small" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Alert
                  severity={pendingCancelled ? 'warning' : 'error'}
                  action={
                    <Button color="inherit" size="small" onClick={dismissFailedTurn}>
                      Dismiss
                    </Button>
                  }
                >
                  {pendingCancelled
                    ? `The ${words.runNoun} was cancelled.`
                    : pendingTask?.error_message || `The ${words.runNoun} failed.`}
                </Alert>
              )}
            </Stack>
          )}

          <Stack spacing={1.5}>
            {attachEnabled && (
              <AttachmentTray
                items={staging.items}
                onRemove={staging.remove}
                onRetry={staging.retry}
                disabled={running}
              />
            )}
            <TextField
              multiline
              minRows={isFollowUp ? 2 : 3}
              label={isFollowUp ? words.followUpLabel : words.questionLabel}
              placeholder={
                isFollowUp
                  ? 'e.g. "And what happened after that?"'
                  : (adapter.questionPlaceholder ??
                    `e.g. "Why is this ${adapter.subjectNoun} in this state?"`)
              }
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onPaste={onQuestionPaste}
              inputProps={{ maxLength: ctx.caps.max_question_chars }}
              helperText={
                subjectNote ??
                `The AI sees only the evidence captured for this ${adapter.subjectNoun} — nothing else.`
              }
              disabled={running}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                select
                label="Model"
                value={model || lastModel || defaultModel}
                onChange={(e) => setModel(e.target.value)}
                sx={{ minWidth: 260 }}
                disabled={running}
                size="small"
              >
                {ctx.models.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label}
                    {m.default ? ' (default)' : ''} — ${m.input_cost_per_mtok}/{m.output_cost_per_mtok} per MTok
                  </MenuItem>
                ))}
              </TextField>
              {ctx.caps.max_budget_usd_per_run != null && (
                <TextField
                  label="Budget"
                  size="small"
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder={String(ctx.caps.budget_usd_per_run)}
                  disabled={running}
                  sx={{ width: 130 }}
                  inputProps={{ min: 0, max: ctx.caps.max_budget_usd_per_run, step: 0.5 }}
                  helperText={`$, max ${ctx.caps.max_budget_usd_per_run}`}
                />
              )}
              {attachEnabled && (
                <Tooltip title="Attach files — or paste a screenshot / drop files on the chat">
                  <span>
                    <IconButton onClick={dropzone.open} disabled={running} aria-label="Attach files">
                      <AttachFileIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="contained"
                startIcon={<PsychologyIcon />}
                disabled={!canSubmit || start.isPending}
                onClick={() => start.mutate({ question: question.trim(), attachments: staging.ready })}
              >
                {running ? words.running : isFollowUp ? words.submitFollowUp : words.submit}
              </Button>
            </Stack>
            {attachEnabled && attachCaps && (
              <Typography variant="caption" color="text.secondary">
                Paste a screenshot, drop files on the chat or use the paperclip — up to{' '}
                {attachCaps.max_per_turn} per question, {formatBytes(attachCaps.max_file_bytes)} each.
                The AI opens them when it needs them. Images are sent as-is: don&apos;t attach
                credentials.
                {staging.uploading && ' Waiting for uploads to finish…'}
                {staging.hasErrors && ' Remove or retry the failed upload to continue.'}
              </Typography>
            )}
          </Stack>
        </Stack>
        </Box>
        )}
      </SectionCard>

      {previousThreads.length > 0 && (
        <SectionCard title="Previous threads">
          <Stack spacing={1}>
            {previousThreads.map((r: BackgroundTaskRecord) => (
              <Stack key={r.id} direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {formatDate(r.created_ts)}
                </Typography>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ flex: 1, minWidth: 0 }}
                  title={(r.params as { question?: string } | null)?.question ?? ''}
                >
                  {(r.params as { question?: string } | null)?.question ?? r.task_label}
                </Typography>
                {r.user_email && (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {r.user_id ? (
                      <ValueActions actions={[act.open(`/users/${r.user_id}`, 'Open user')]}>
                        {r.user_email}
                      </ValueActions>
                    ) : (
                      r.user_email
                    )}
                  </Typography>
                )}
                {/* "Open" keeps its own click — it swaps the thread shown in
                    this panel; the popover only adds the task record. */}
                <ValueActions actions={[act.open(`/bgtasks/${r.id}`, 'Open the background task')]}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    sx={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      setPending(null);
                      setQuestion('');
                      staging.reset();
                      setAnchorId(r.id);
                    }}
                  >
                    Open
                  </Link>
                </ValueActions>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}
    </Box>
  );
}
